import { supabase } from '../lib/supabase/client';
import { Room } from '../types/models';
import { Database } from '../types/database.types';

type RoomRow = Database['public']['Tables']['rooms']['Row'];

// Convert snake_case database row to camelCase application model
function convertToRoom(row: RoomRow): Room {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    roomType: row.room_type,
    description: row.description,
    pricePerNight: row.price_per_night,
    maxGuests: row.max_guests,
    sizeSqm: row.size_sqm,
    features: row.features || [],
    amenities: row.amenities || [],
    images: row.images || [],
    isAvailable: row.is_available,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const roomService = {
  async getAll(): Promise<{ data: Room[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('is_available', true)
        .order('name', { ascending: true });

      if (error) throw error;

      const rooms = data?.map(convertToRoom) || [];
      return { data: rooms, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getBySlug(slug: string): Promise<{ data: Room | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;

      const room = data ? convertToRoom(data) : null;
      return { data: room, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async checkAvailability(
    roomId: string,
    checkIn: string,
    checkOut: string
  ): Promise<{ data: boolean | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc('is_room_available', {
        room_uuid: roomId,
        start_date: checkIn,
        end_date: checkOut,
      });

      if (error) throw error;

      return { data: data ?? false, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async calculatePrice(
    roomId: string,
    checkIn: string,
    checkOut: string
  ): Promise<{ data: number | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc('calculate_booking_price', {
        room_uuid: roomId,
        start_date: checkIn,
        end_date: checkOut,
      });

      if (error) throw error;

      return { data: data ?? 0, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};